import type { DateTime, Utc } from "@tento-chrono";
import { EventItemPresentation } from "@tento-lona";
import { EventItemTimed, type Option } from "@tento-lona/sheets";
import type { SheetModelReads } from "@tento-lona/sheets";
import { GestureItem } from "@tento-ui/components/calendar/calendar-gesture";
import type {
  HasContextMenu,
  ItemActionContext,
  Movable,
  OpensDetails,
} from "./interactable-item.ts";
import type { ContextMenuItemDelegate } from "./existing-item-delegate.ts";

/**
 * App-layer hooks for UI concerns the SDK can't drive: showing event
 * details and opening context menus (the drag ghost routes through
 * `RendererGestureHost.ghost`, not this delegate). Persistence
 * is also delegate-driven for now — the application wires the
 * `persistTimeChange` impl with access to the rows-side
 * `sheet.canonicalCells` (the same store the renderer reads from)
 * and the SDK's events API. Phase 2.9 U3 design: the dispatch
 * doesn't depend on `EventsIndex.update` routing through the
 * JoinedClient `RowCellCache._cells` store, which silently diverges
 * from rows-side `canonicalCells`. See `architectural-framing.md`.
 *
 * Events don't extend `NewItemActions` — create flow routes through
 * the column subcolumn's new-event actions (not the gesture item).
 */
export interface EventItemDelegate
  extends ContextMenuItemDelegate<EventItemTimed> {
  /** Read-only Google calendars surface events that can't be moved. */
  isEditable(event: EventItemTimed): boolean;
  /**
   * Persist a time change. `sourceSheet` is the rows-side sheet the
   * event was rendered from, as its reads (`SheetModelReads`: rows,
   * canonical cells). Null when no rows-side sheet is attached
   * (legacy / test fixtures); the impl persists through the SDK.
   */
  persistTimeChange(
    event: EventItemTimed,
    newTimeRange: DateTime.Range<Utc>,
    sourceSheet: Option<SheetModelReads>,
  ): Promise<Option<EventItemTimed>>;
  /**
   * Open `event`'s details with `access`: `"read-only"` opens the panel with
   * no edits, RSVP, move or delete, whatever the calendar allows (a
   * read-only calendar list). `showDetails` is the `"editable"` case.
   */
  showDetailsWith(event: EventItemTimed, context: ItemActionContext, access: "editable" | "read-only"): void;
}

/**
 * `base` as a read-only calendar list exposes it (N4-1 parity): events still
 * open their details, read-only, but never move or resize (`isEditable` is
 * false, so the move/resize gestures refuse them), never persist a time
 * change, and offer no context menu (every row of it writes).
 */
export function readOnlyEventItemDelegate(base: EventItemDelegate): EventItemDelegate {
  return {
    isEditable: () => false,
    persistTimeChange: async () => null,
    showDetails: (event, context) => base.showDetailsWith(event, context, "read-only"),
    showDetailsWith: (event, context) => base.showDetailsWith(event, context, "read-only"),
    dismissDetails: () => base.dismissDetails(),
    isDetailsRevealed: () => base.isDetailsRevealed(),
    showContextMenu: () => {},
    dismissContextMenu: () => base.dismissContextMenu(),
  };
}

/** GestureItem subclass for calendar events — supports the full
 *  gesture set (Move, Resize, ClickToOpenDetails, ContextMenu). */
export class EventGestureItem extends GestureItem
  implements Movable, OpensDetails, HasContextMenu {
  readonly #sourceSheet: Option<SheetModelReads>;
  readonly #delegate: EventItemDelegate;
  readonly #presentation: EventItemPresentation.Scope;
  /** `presentation`: the client's event categories and drafts (`client.events`), for `title` / `backgroundColor`. */
  constructor(
    readonly event: EventItemTimed,
    delegate: EventItemDelegate,
    sourceSheet: Option<SheetModelReads>,
    presentation: EventItemPresentation.Scope,
  ) {
    super();
    this.#delegate = delegate;
    this.#sourceSheet = sourceSheet;
    this.#presentation = presentation;
  }

  get id(): string { return this.event.id; }
  get timeRange(): DateTime.Range<Utc> { return this.event.time.time; }
  get title(): string { return EventItemPresentation.title(this.event, this.#presentation); }
  get backgroundColor(): string { return EventItemPresentation.backgroundColor(this.event, this.#presentation); }
  get isEditable(): boolean { return this.#delegate.isEditable(this.event); }

  showDetails(context: ItemActionContext): void {
    this.#delegate.showDetails(this.event, context);
  }

  dismissDetails(): void {
    this.#delegate.dismissDetails();
  }

  isDetailsRevealed(): boolean {
    return this.#delegate.isDetailsRevealed();
  }

  showContextMenu(context: ItemActionContext, offset: { x: number; y: number }): void {
    this.#delegate.showContextMenu(this.event, context, offset);
  }

  dismissContextMenu(): void {
    this.#delegate.dismissContextMenu();
  }

  async persistTimeChange(
    newTimeRange: DateTime.Range<Utc>,
    _x: Option<number>,
  ): Promise<Option<EventGestureItem>> {
    const updated = await this.#delegate.persistTimeChange(
      this.event,
      newTimeRange,
      this.#sourceSheet,
    );
    if (!updated) return null;
    return new EventGestureItem(updated, this.#delegate, this.#sourceSheet, this.#presentation);
  }
}
